import { createContext, useContext, type ReactNode } from 'react';

import type { Source } from './sourcePanel.types';

/**
 * Carries only the opener, never the selected source. A citation badge renders far below the
 * page and cannot be reached by a prop, but it only ever writes; the panel that reads lives
 * beside the page's own state. Keeping the value write-only means opening a source does not
 * invalidate the context for the many `AnalysisMarkdown` blocks subscribed to it.
 *
 * The citation passes itself as `trigger` so focus can return there when the panel closes.
 */
type OpenSource = (source: Source, trigger?: HTMLElement | null) => void;

/**
 * `null` outside a provider, rather than a no-op that silently swallows the click. Surfaces
 * that show the report without a panel to open it in — the project Library preview — get a
 * citation that links straight to its source instead of a control that does nothing.
 */
const SourcePanelContext = createContext<OpenSource | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its provider
export const useOpenSource = () => useContext(SourcePanelContext);

/**
 * Publishes the page's `open` handler to the citations nested under it. One source at a time
 * for the whole page, so opening a second citation replaces the first rather than stacking
 * panels; that is the page's `useState`, not something held here.
 *
 * Pass a stable `onOpen` — an unmemoised inline arrow re-renders every citation on the page.
 */
export const SourcePanelProvider = ({
  onOpen,
  children,
}: {
  onOpen: OpenSource;
  children: ReactNode;
}) => <SourcePanelContext.Provider value={onOpen}>{children}</SourcePanelContext.Provider>;
